import React from "react";
import { CvExportOptionsModal } from "../../../components/common/bulk-export";
import { ExportService } from "../../../services/exportService";
import { useTranslation } from "../../../hooks/useTranslation";

const exportService = new ExportService();

export interface ReportCvExportModalProps {
  /** Which trigger opened this — "selected" (current page's checked rows) or "all" (every matching person). Null renders nothing. */
  mode: "selected" | "all" | null;
  onClose: () => void;
  selectedIds: Set<number>;
  totalCount: number;
  /** Resolves every PersonId matching the current report filter, unpaged — backs the "all" mode. */
  getMatchingPersonIds: () => Promise<number[]>;
  /** e.g. "CVs_CertificatesReport" / "CVs_TechnologyReport" */
  fileNamePrefix: string;
  /** Mirrors a review-and-prune outcome back onto the current page's row checkboxes ("selected" mode only). */
  onSelectedIdsChanged?: (ids: number[]) => void;
}

/**
 * The two "Export CV" triggers shared by both report pages (By Technology, By Certificates) — reuses
 * the same export options popup as the CV list's bulk export (format/language/anonymization, tier
 * warnings, hard-block, optional review-and-prune) instead of a bespoke, thinner flow per report.
 */
export const ReportCvExportModal: React.FC<ReportCvExportModalProps> = ({
  mode,
  onClose,
  selectedIds,
  totalCount,
  getMatchingPersonIds,
  fileNamePrefix,
  onSelectedIdsChanged,
}) => {
  const { t } = useTranslation();

  if (mode === "selected") {
    const selectedCount = selectedIds.size;
    return (
      <CvExportOptionsModal
        opened
        onClose={onClose}
        title={t("BULK_MODAL_TITLE_SELECTED", { count: selectedCount.toString(), suffix: selectedCount !== 1 ? "s" : "" })}
        count={selectedCount}
        countMode="selected"
        fileNamePrefix={`${fileNamePrefix}_Selected`}
        onExport={(reviewedIds, anonymization, language, format) =>
          exportService.bulkExportByIds(reviewedIds ?? Array.from(selectedIds), anonymization, language, format)
        }
        review={
          onSelectedIdsChanged
            ? {
                getIds: () => Array.from(selectedIds),
                onIdsChanged: onSelectedIdsChanged,
              }
            : undefined
        }
      />
    );
  }

  if (mode === "all") {
    return (
      <CvExportOptionsModal
        opened
        onClose={onClose}
        title={t("BULK_MODAL_TITLE_ALL", { count: totalCount.toString(), suffix: totalCount !== 1 ? "s" : "" })}
        count={totalCount}
        countMode="all-found"
        fileNamePrefix={`${fileNamePrefix}_AllMatching`}
        onExport={async (reviewedIds, anonymization, language, format) => {
          const ids = reviewedIds ?? (await getMatchingPersonIds());
          return exportService.bulkExportByIds(ids, anonymization, language, format);
        }}
        review={{ getIds: getMatchingPersonIds }}
      />
    );
  }

  return null;
};
